<template>
  <div class="global-search" :class="{ 'is-open': showPanel }">
    <div ref="field" class="global-search__field">
      <fa icon="search" class="global-search__icon" fixed-width />
      <input
        ref="input"
        v-model="query"
        type="search"
        class="form-control global-search__input"
        placeholder="Search products, brands, categories…"
        autocomplete="off"
        aria-label="Search"
        @input="onInput"
        @focus="onFocus"
        @click="onFocus"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="close(true)"
      >
    </div>

    <template v-if="showPanel">
      <div class="global-search__backdrop" :style="{ top: panelTop + 'px' }" @mousedown="close" />

      <div class="global-search__panel" :style="{ top: panelTop + 'px' }">
        <div class="global-search__inner">
          <!-- Recent searches -->
          <template v-if="panelMode === 'history'">
            <div class="global-search__recent">
              <div class="global-search__section-head">
                <span>Recent searches</span>
                <button type="button" class="global-search__clear" @mousedown.prevent="clearAllHistory">
                  Clear all
                </button>
              </div>
              <ul class="global-search__history">
              <li
                v-for="(item, i) in recent"
                :key="'h-' + i"
                class="global-search__row global-search__row--history"
                :class="{ 'is-active': activeIndex === i }"
                @mouseenter="activeIndex = i"
                @mousedown.prevent="applyHistory(item)"
              >
                <fa :icon="historyIcon(item)" class="global-search__row-icon" fixed-width />
                <span class="global-search__row-label">{{ item.label }}</span>
                <button
                  type="button"
                  class="global-search__remove"
                  aria-label="Remove"
                  @mousedown.prevent.stop="removeHistoryItem(item)"
                >
                  <fa icon="times" fixed-width />
                </button>
              </li>
              </ul>
            </div>
          </template>

          <!-- In flight -->
          <div v-else-if="panelMode === 'loading'" class="global-search__status">
            <fa icon="spinner" spin fixed-width />
            <span>Searching…</span>
          </div>

          <!-- Results -->
          <template v-else-if="panelMode === 'results'">
            <div v-if="searchBrands.length || searchCategories.length" class="global-search__taxonomy">
              <div v-if="searchBrands.length" class="global-search__col">
                <div class="global-search__group-title">Brands</div>
                <ul class="global-search__list">
                  <li
                    v-for="b in searchBrands"
                    :key="'b-' + b.id"
                    class="global-search__row"
                    :class="{ 'is-active': isActive('brand', b.id) }"
                    @mouseenter="setActiveByRef('brand', b.id)"
                    @mousedown.prevent="selectBrand(b)"
                  >
                    <fa icon="tag" class="global-search__row-icon" fixed-width />
                    <span class="global-search__row-label" v-html="highlight(b.name)" />
                  </li>
                </ul>
              </div>

              <div v-if="searchCategories.length" class="global-search__col">
                <div class="global-search__group-title">Categories</div>
                <ul class="global-search__list">
                  <li
                    v-for="c in searchCategories"
                    :key="categoryRef(c)"
                    class="global-search__row"
                    :class="{ 'is-active': isActive('category', categoryRef(c)) }"
                    @mouseenter="setActiveByRef('category', categoryRef(c))"
                    @mousedown.prevent="selectCategory(c)"
                  >
                    <fa icon="layer-group" class="global-search__row-icon" fixed-width />
                    <span class="global-search__row-label">
                      <span v-for="(seg, si) in c.path" :key="si" class="global-search__crumb">
                        <span v-html="highlight(seg)" /><span
                          v-if="si < c.path.length - 1"
                          class="global-search__crumb-sep"
                        > / </span>
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div v-if="searchProducts.length" class="global-search__products-section">
              <div class="global-search__group-title">Products</div>
              <div class="global-search__products">
                <SearchProductCard
                  v-for="p in searchProducts"
                  :key="'p-' + p.id"
                  :product="p"
                  class="global-search__product"
                  @navigate="selectProduct"
                />
              </div>
            </div>

            <button
              v-if="searchProductsTotal > 0"
              type="button"
              class="global-search__view-all"
              :class="{ 'is-active': activeIndex === navItems.length - 1 }"
              @mouseenter="activeIndex = navItems.length - 1"
              @mousedown.prevent="submitFreeText"
            >
              View all {{ searchProductsTotal }} {{ searchProductsTotal === 1 ? 'result' : 'results' }}
            </button>
          </template>

          <!-- Searched, nothing matched -->
          <div v-else-if="panelMode === 'empty'" class="global-search__status">
            <span>No results for “{{ query.trim() }}”</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from 'lodash'
import SearchProductCard from './SearchProductCard'
import { historyKey } from '~/helpers/search-history'

const MIN_CHARS = 2

export default {
  name: 'GlobalSearch',

  components: { SearchProductCard },

  data () {
    return {
      query: '',
      open: false,
      activeIndex: -1,
      // Viewport Y where the full-width panel starts (just below the search field).
      panelTop: 0,
      // True from a valid keystroke until the store settles, so the panel shows
      // "Searching…" during the debounce gap instead of flashing stale results.
      pendingInput: false
    }
  },

  computed: {
    ...mapGetters({
      searchLoading: 'search/searchLoading',
      searchBrands: 'search/searchBrands',
      searchCategories: 'search/searchCategories',
      searchProducts: 'search/searchProducts',
      searchProductsTotal: 'search/searchProductsTotal',
      searchHasResults: 'search/searchHasResults',
      searchHasSearched: 'search/searchHasSearched',
      recent: 'search_history/recentSearches'
    }),

    trimmed () {
      return this.query.trim().replace(/\s+/g, ' ')
    },

    displayLoading () {
      return this.searchLoading || this.pendingInput
    },

    panelMode () {
      if (this.trimmed.length < MIN_CHARS) return 'history'
      if (this.displayLoading) return 'loading'
      if (this.searchHasResults) return 'results'
      return 'empty'
    },

    showPanel () {
      if (!this.open) return false
      if (this.panelMode === 'history') return this.recent.length > 0
      return true
    },

    // Flat, ordered list of keyboard-selectable rows for the current panel — index
    // parity with the arrow-key cursor. "View all" is the trailing row when present.
    navItems () {
      if (this.panelMode === 'history') {
        return this.recent.map((item) => ({ kind: 'history', item }))
      }
      if (this.panelMode !== 'results') return []
      const items = [
        ...this.searchBrands.map((b) => ({ kind: 'brand', ref: b.id, data: b })),
        ...this.searchCategories.map((c) => ({ kind: 'category', ref: this.categoryRef(c), data: c })),
        ...this.searchProducts.map((p) => ({ kind: 'product', ref: p.id, data: p }))
      ]
      if (this.searchProductsTotal > 0) items.push({ kind: 'viewAll' })
      return items
    }
  },

  watch: {
    // When the newest request settles the panel drops out of "loading".
    searchLoading (loading) {
      if (!loading) this.pendingInput = false
    },
    // Any navigation closes the panel (e.g. the checkout redirect a result-card order
    // toggle triggers). A same-route refresh fires no route change, so the panel stays
    // open there and the user can keep adding.
    $route () {
      if (this.open) this.close(true)
    }
  },

  created () {
    this.debouncedSearch = debounce((term) => {
      this.$store.dispatch('search/runSearch', term)
    }, 300)
  },

  mounted () {
    // Account-based history: seed from the per-user localStorage cache (instant) and
    // reconcile with the server in the background. Guarded to run once per session.
    this.hydrateSearchHistory()
    document.addEventListener('mousedown', this.onDocumentMousedown)
    window.addEventListener('resize', this.updatePanelPosition)
    window.addEventListener('scroll', this.updatePanelPosition, true)
  },

  beforeDestroy () {
    document.removeEventListener('mousedown', this.onDocumentMousedown)
    window.removeEventListener('resize', this.updatePanelPosition)
    window.removeEventListener('scroll', this.updatePanelPosition, true)
    this.debouncedSearch.cancel()
  },

  methods: {
    ...mapActions('search_history', {
      hydrateSearchHistory: 'hydrate',
      addSearchHistory: 'add',
      removeSearchHistory: 'remove',
      clearAllSearchHistory: 'clearAll'
    }),

    updatePanelPosition () {
      const field = this.$refs.field
      if (field) this.panelTop = field.getBoundingClientRect().bottom
    },

    onFocus () {
      this.open = true
      this.updatePanelPosition()
    },

    onInput () {
      this.activeIndex = -1
      const term = this.trimmed
      if (term.length < MIN_CHARS) {
        this.pendingInput = false
        this.debouncedSearch.cancel()
        this.$store.dispatch('search/resetSearch')
        return
      }
      this.pendingInput = true
      this.debouncedSearch(term)
    },

    onDocumentMousedown (event) {
      if (!this.open) return
      // A click inside the search itself never closes it.
      if (this.$el.contains(event.target)) return
      // Nor does a click inside a modal launched from the dropdown (e.g. the New Order
      // modal from a product's Order button when there's no active draft order) — the
      // search stays open behind it so the user's results survive completing the order.
      if (event.target.closest && event.target.closest('.vm--container')) return
      this.close()
    },

    move (delta) {
      const len = this.navItems.length
      if (!len) return
      this.open = true
      this.activeIndex = (this.activeIndex + delta + len) % len
    },

    onEnter () {
      const item = this.navItems[this.activeIndex]
      if (item) {
        this.selectNavItem(item)
      } else if (this.trimmed.length >= MIN_CHARS) {
        this.submitFreeText()
      }
    },

    selectNavItem (item) {
      switch (item.kind) {
        case 'history': return this.applyHistory(item.item)
        case 'brand': return this.selectBrand(item.data)
        case 'category': return this.selectCategory(item.data)
        case 'product': return this.selectProduct(item.data)
        case 'viewAll': return this.submitFreeText()
      }
    },

    isActive (kind, ref) {
      const item = this.navItems[this.activeIndex]
      return item && item.kind === kind && item.ref === ref
    },

    setActiveByRef (kind, ref) {
      this.activeIndex = this.navItems.findIndex((i) => i.kind === kind && i.ref === ref)
    },

    // ---- result selection: navigate the existing catalog way + remember it ----

    goToProducts (routeQuery, historyItem) {
      this.addSearchHistory(historyItem)
      // Clear the field + results so re-focusing the search shows "Recent searches"
      // (with what was just used) instead of re-running the old query.
      this.query = ''
      this.pendingInput = false
      this.$store.dispatch('search/resetSearch')
      // Blur so the field isn't left focused on the destination page — otherwise the
      // next click on it fires no `focus` event and the recent-searches panel won't open.
      this.close(true)
      this.$router.push({ name: 'products', query: routeQuery }).catch(() => {})
    },

    selectBrand (brand) {
      this.goToProducts(
        { brand: String(brand.id) },
        { type: 'brand', navKey: 'brand', id: brand.id, label: brand.name }
      )
    },

    // A taxonomy row is identified by its whole chain, not the matched id: five departments
    // named Neckwear under different brands are five distinct rows, and type + id would give
    // them all the same v-for key and the same keyboard-nav ref. historyKey already spells
    // that identity for recent searches, so the list and the history agree by construction.
    categoryRef (cat) {
      return historyKey({ type: 'category', navKey: cat.type, id: cat.id, query: cat.query })
    },

    selectCategory (cat) {
      // The row displays a full path (Bocara / Bocara Men / Socks), so navigate to that whole
      // chain rather than the deepest id alone — cat.query carries it, in products-page param
      // names. A bare ?department=42 names no brand, which is both a different product set and
      // a query the page header cannot resolve to a node. Same shape the mega menu pushes.
      const query = cat.query || { [cat.type]: cat.id }

      this.goToProducts(
        { ...query },
        {
          type: 'category',
          navKey: cat.type,
          id: cat.id,
          query,
          label: (cat.path || []).join(' / ') || cat.name
        }
      )
    },

    selectProduct (product) {
      // Navigate by the COMPLETE design (base + colour, e.g. MC20-1062A). The products-page
      // search matches the reconstructed complete design exactly, so this lands on the single
      // colour variant rather than every colour of the base design.
      const complete = product.design_id || product.design_number
      this.goToProducts(
        { search: complete },
        {
          type: 'product',
          designId: product.design_id,
          designNumber: product.design_number,
          productId: product.id,
          label: complete
        }
      )
    },

    submitFreeText () {
      const term = this.trimmed
      if (term.length < MIN_CHARS) return
      this.goToProducts(
        { search: term },
        { type: 'term', term, label: term }
      )
    },

    // ---- recent-searches management ----

    applyHistory (item) {
      switch (item.type) {
        case 'brand':
        case 'category':
          // Replaying must reproduce the original click exactly. Rows stored before the
          // chain existed carry only navKey + id, so fall back to that shape for them.
          return this.goToProducts(item.query ? { ...item.query } : { [item.navKey]: String(item.id) }, item)
        case 'product':
          return this.goToProducts({ search: item.designId || item.designNumber }, item)
        case 'term':
          return this.goToProducts({ search: item.term }, item)
      }
    },

    removeHistoryItem (item) {
      this.removeSearchHistory(historyKey(item))
    },

    clearAllHistory () {
      this.clearAllSearchHistory()
    },

    historyIcon (item) {
      if (item.type === 'brand') return 'tag'
      if (item.type === 'category') return 'layer-group'
      if (item.type === 'product') return 'box'
      return 'history'
    },

    // ---- misc ----

    highlight (text) {
      const term = this.trimmed
      if (!term) return this.escapeHtml(text)
      const safe = this.escapeHtml(String(text))
      const pattern = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safe.replace(new RegExp('(' + pattern + ')', 'ig'), '<strong>$1</strong>')
    },

    escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },

    close (blur = false) {
      this.open = false
      this.activeIndex = -1
      if (blur && this.$refs.input) this.$refs.input.blur()
    }
  }
}
</script>

<style scoped lang="scss">
.global-search {
  position: relative;
  width: 100%;

  &__field {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__icon {
    position: absolute;
    left: 10px;
    color: #898989;
    pointer-events: none;
    z-index: 1;
  }

  &__input {
    padding-left: 32px;
    height: 38px;
    border-radius: 2px;
  }

  // Dim the page behind the full-width overlay (Mr Porter style).
  &__backdrop {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.28);
    z-index: 1049;
  }

  // Full-viewport-width panel dropping below the header.
  &__panel {
    position: fixed;
    left: 0;
    right: 0;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
    background: #fff;
    border-top: 1px solid #e3e2e2;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14);
    z-index: 1050;
    color: #383838;
    font: normal normal normal 13px/18px Arial;
  }

  &__inner {
    max-width: 1440px;
    margin: 0 auto;
    padding: 24px 40px 32px;

    @media (max-width: 800px) {
      padding: 16px 20px 24px;
    }
  }

  &__section-head,
  &__group-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-size: 11px;
    font-weight: 700;
    color: #898989;
    margin: 0 0 12px;
  }

  // Brands + Categories side by side above the products.
  &__taxonomy {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 40px;
    padding-bottom: 20px;
    margin-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  // Recent-searches spans the full panel width so every row's × remove icon lands at the
  // far right — directly under the "Clear all" button — consistent on any screen size.
  &__recent {
    width: 100%;
  }

  &__history {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 8px;
    cursor: pointer;
    border-radius: 3px;

    &.is-active,
    &:hover {
      background: #f5f5f5;
    }

    &-icon {
      color: #b0b0b0;
      flex-shrink: 0;
    }

    &-label {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__crumb-sep {
    color: #b0b0b0;
  }

  &__remove {
    border: 0;
    background: transparent;
    color: #b0b0b0;
    cursor: pointer;
    padding: 2px 4px;

    &:hover {
      color: #383838;
    }
  }

  &__clear {
    border: 0;
    background: transparent;
    color: #31b4d6;
    font-size: 11px;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    padding: 0;
  }

  // Wide product row, like Mr Porter's PRODUCTS strip.
  &__products {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 24px;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media (max-width: 800px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 28px 8px;
    color: #898989;
    justify-content: center;
  }

  &__view-all {
    display: block;
    margin: 24px auto 0;
    padding: 12px 40px;
    border: 1px solid #383838;
    background: #fff;
    color: #383838;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 12px;
    cursor: pointer;

    &.is-active,
    &:hover {
      background: #383838;
      color: #fff;
    }
  }
}
</style>
